/**
 *
 * CommFormBuilder
 *
 */

import React, { memo, useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useLocation } from 'react-router-dom';

// styles
import './style.scss';

// antd component
import { Form } from 'antd';
// inner communication form components
import FBAddNewSection from './components/FBAddNewSection';
import FBForms from './components/FBForms';
import FBAppointment from './components/FBAppointment';
import FBDocuments from './components/FBDocuments';
import FBDiscussion from './components/FBDiscussion';

// hooks
import { useRefState } from 'utils/hooks';
// utils
import {
  objectsEqual,
  arraysObjEqual,
  ArrayEquals,
  objectsAreSameByKeys,
  filterObject
} from 'utils/helpers/helper.general';


function CommFormBuilder({
  saveToForm,
  mode,
  method,
  initialValues,
  values,
  name,
  // actions
  onSaveForm,
  onValuesChange,
  // default props
  className,
  ...rest
}) {
  const childClassNames = classNames(
    'communication-form-builder-wrapper',
    className,
  );

  const location = useLocation();

  const newKey = 'nouvel';

  const init = {
    forms: null,
    appointments: null,
    documents: null,
    discussion: null,
  };
  const initValidation = {
    forms: true,
    appointments: true,
    documents: true,
    discussion: true,
  };

  const [firstLoad, setFirstLoad] = useState(false);
  const [defaultValue, setDefaultValue] = useState(null);
  const [buildFormValid, buildFormValidRef, setBuildFormValid] = useRefState(
    true,
  );
  const [sectionsIsValid, sectionsIsValidRef, setSectionsIsValid] = useRefState(
    initValidation,
  );
  const [
    formBuilderAddedTypes,
    formBuilderAddedTypesRef,
    setFormBuilderAddedTypes,
  ] = useRefState(values?.types || []);

  // Helper function
  const prepearValToSet = useCallback(
    vals => {
      const data = init;
      if (!!vals && Object.keys(vals)?.length > 0) {
        for (const initKey in init) {
          const val = vals[initKey];
          if (
            !!val &&
            Object.keys(val)?.length > 0 &&
            initKey != 'discussion'
          ) {
            data[initKey] = vals[initKey];
          }
        }
      }
      return data;
    },
    [init, values],
  );

  const [
    formBuilderStates,
    formBuilderStatesRef,
    setFormBuilderStates,
  ] = useRefState(
    prepearValToSet(values) || prepearValToSet(initialValues) || init,
  );

  // Helper function
  const validateSectionFieldsValues = useCallback((name, values) => {
    let hasValid = false;
    if (
      name &&
      (!!values &&
        Object.keys(values)?.length > 0 &&
        values?.[`${name}-fields`] &&
        Array.isArray(values[`${name}-fields`]) &&
        values[`${name}-fields`]?.length > 0 &&
        values[`${name}-fields`][0])
    ) {
      hasValid = true;
    } else {
      hasValid = false;
    }
    return hasValid;
  }, []);
  // Helper function
  const compareFormFieldsAndState = useCallback(
    formVal => {
      let formValues = formVal;
      const formKeys = Object.keys(formValues);
      let updateFromVals = formBuilderStates;

      // check the data we get from the build form (usually the data from the state is more correct, but from the form values ​​we get the new value we enter)
      formKeys?.map(key => {
        // create new copy
        let fV = formValues;

        // compare & replace form data and state data
        const fileds = fV[key]?.[`${key}-fields`] || [];
        const formFields = fileds?.length > 0 ? fileds.filter(el => !!el) : []; // normalize, filter empty value
        const stateFields = formBuilderStates[key][`${key}-fields`];
        const fieldsEqual = arraysObjEqual(formFields, stateFields);
        const isEqual = objectsEqual(fV[key], formBuilderStates[key]);
        if (!isEqual) {
          updateFromVals = {
            ...updateFromVals,
            [key]: { ...updateFromVals[key], ...formValues[key] },
          };
          if (!fieldsEqual && formFields?.length == stateFields?.length) {
            const fileds = stateFields?.map((f, inx) => ({
              ...f,
              ...formValues[key][`${key}-fields`][inx],
            }));
            updateFromVals[key][`${key}-fields`] = fileds;
          }
        }
      });

      return updateFromVals;
    },
    [formBuilderStates],
  );
  const getTypesFromValues = useCallback(vals => {
    const values = [];
    if (!!vals && Object.keys(vals)?.length > 0) {
      for (const initKey in init) {
        const val = vals[initKey];
        if (!!val && Object.keys(val)?.length > 0 && initKey != 'discussion') {
          values.push(initKey);
        }
      }

      // Only for Discussion Section
      const hasD = !!vals?.types?.length > 0 && vals?.types?.find(el => el == 'discussion');
      if (!!hasD) {
        values.push('discussion');
      }
    }
    return values;
  }, []);

  const checkIsSameData = useCallback(
    data => {
      let equal = true;
      if (defaultValue?.status != 'sent') {
        if (mode == 'edit' || mode == 'new') {
          const allValuesObj = Object.assign(data);
          const formValsKeys = Object.keys(data)?.filter(k => k != 'types' && k != 'discussion');
          const formVals = Object.values(data);
          const formHasAnyVals = formVals.some(
            val => val != '' && val != undefined && val != null,
          );
          const comareObjFormData = filterObject(allValuesObj, formValsKeys);
          const comareObjValData = filterObject(defaultValue, formValsKeys);
          const isEqualValues = objectsAreSameByKeys(
            comareObjValData,
            comareObjFormData,
            formValsKeys,
          );

          if (!!formHasAnyVals && !!!defaultValue) {
            equal = false;
          } else {
            equal = isEqualValues;
          }
        } else if (mode == 'new' && method == newKey) {
          equal = false;
        } 
      } else {
        equal = false;
      }

      return equal;
    },
    [method, mode, values, defaultValue],
  );

  // Clear states on mount component
  useEffect(() => {
    setFirstLoad(true);
    // main code here
    const fromPreview = location?.state?.from == 'preview_communication';
    !!values && setDefaultValue(values);
    return () => {
      if (!fromPreview && (method == newKey || mode == 'edit' || mode == 'new')) {
        resetAllStates();
      }
    };
  }, []);

  // Refresh current value based on parent`s states
  useEffect(() => {
    if (!!values) {
      setFirstLoad(false);
      !!firstLoad && setDefaultValue(values);
      const newVals = prepearValToSet(values);

      if (JSON.stringify(values) != JSON.stringify(formBuilderStates)) {
        setFormBuilderStates(newVals);
      }

      const types = getTypesFromValues(values);
      const typesEquals = ArrayEquals(types, formBuilderAddedTypes);
      (types?.length == 0 || !typesEquals) && setFormBuilderAddedTypes(types);
    } else if (!!!values) {
      resetAllStates();
    }
  }, [values]);

  // Sent validation status of build form to the parent component
  useEffect(() => {
    if (!!sectionsIsValid) {
      const validInfo = onValidateSections();
      const { isValid, type } = validInfo;
      const validation = !!type ? isValid : true;

      if (buildFormValid != validation) {
        setBuildFormValid(validation);

        const sentData = {
          sectionName: type,
          buildFormValid: validation,
          data: {
            ...formBuilderStatesRef?.current,
            types: formBuilderAddedTypesRef?.current,
          },
        };
  
        const sameVal = checkIsSameData(sentData.data);
        if ((mode == 'edit' || mode == 'new') && values?.status != 'sent') {
          !!onValuesChange && onValuesChange({ ...sentData, valsEqual: sameVal });
        }
      }
    
    }
  }, [sectionsIsValid]);

  const onValidateSections = useCallback(() => {
    let isValid = true;
    let notValid = '';
    if (!!sectionsIsValid) {
      const sectionKeysArr = Object.entries(sectionsIsValidRef?.current);
      const sectionKeys = sectionKeysArr
        ?.map(el => formBuilderAddedTypes.includes(el[0]) && el[0])
        .filter(el => !!el);
      const keysIsSame = ArrayEquals(sectionKeys, formBuilderAddedTypes);
      if (
        keysIsSame &&
        sectionKeys?.length > 0 &&
        formBuilderAddedTypes?.length > 0
      ) {
        notValid =
          sectionKeys?.length > 0 &&
          sectionKeys?.find(elKey => !!!sectionsIsValidRef?.current[elKey]);
        if (!!notValid) {
          // sent to parent component
          isValid = false;
        }
      } else {
        isValid = true;
      }
    }

    return {
      isValid,
      type: notValid,
    };
  }, [sectionsIsValid, formBuilderAddedTypes]);

  const onChangeSectioValidation = useCallback((name, status) => {
    setSectionsIsValid(prev => ({
      ...prev,
      [name]: status
    }))
  }, [])

  const resetAllStates = useCallback(() => {
    // clear states
    setSectionsIsValid(initValidation);
    setFormBuilderAddedTypes([]);
    setFormBuilderStates(init);
    setBuildFormValid(true);
  }, [method, mode, values]);

  // Add new Section, only for <FBAddNewSection />
  const addNewTypeSectionHandler = useCallback(
    addedTypes => {
      if (Array.isArray(addedTypes) && addedTypes?.length > 0) {
        // generate default value for each type form
        addedTypes?.map(key => {
          !!key && generateDefaultValuesByType(key);
        });

        // Only for Discussion Section
        const prevHasDiss = formBuilderAddedTypes?.find(
          el => el == 'discussion',
        );
        const currentHasDiss = addedTypes?.find(el => el == 'discussion');
        if (!prevHasDiss && !!currentHasDiss) {
          const validInfo = onValidateSections();
          const { isValid, type } = validInfo;
          const validation = !!type ? isValid : true;
          setBuildFormValid(validation);
          const sentData = {
            sectionName: 'discussion',
            buildFormValid: validation,
            data: {
              ...formBuilderStatesRef?.current,
              types: addedTypes,
            },
          };
          if ((mode == 'edit' || mode == 'new') && values?.status != 'sent') {
            !!onValuesChange &&
              onValuesChange({ ...sentData, valsEqual: false });
          }
        }

        // set current checked type of form
        setFormBuilderAddedTypes(addedTypes);
      }
    },
    [formBuilderAddedTypes, mode, values, formBuilderStatesRef],
  );

  const generateDefaultValuesByType = useCallback(
    type => {
      if (type && type != 'discussion') {
        const intKeys = Object.keys(init);
        const hasProps = intKeys.includes(type);

        const defaultObj = {
          [`${type}-title`]: '',
          [`${type}-description`]: '',
          [`${type}-fields`]: [],
        };

        if (!!!hasProps && intKeys?.length > 0) {
          return defaultObj;
        }

        let isValidStates = false;
        if (Object.keys(formBuilderStates)?.length > 0) {
          isValidStates = validateSectionFieldsValues(
            type,
            formBuilderStates[type],
          );
        }

        const ObjV = (isValidStates && formBuilderStates[type]) || defaultObj;

        // set default values
        setFormBuilderStates(prev => {
          let prevP = prev || {};
          prevP = {
            ...prevP,
            [type]: ObjV,
          };
          return prevP;
        });

        // validate builder form after we add new type
        onValidateBuilderForm(type, {
          [type]: ObjV,
        });

        return ObjV;
      }
    },
    [formBuilderStates],
  );

  // delete section type
  const deleteSectionType = useCallback(
    sectionName => {
      if (sectionName) {
        setFormBuilderStates(prev => ({ ...prev, [sectionName]: null }));
        const filteredTypes = formBuilderAddedTypesRef?.current?.filter(
          el => el != sectionName,
        );

        setFormBuilderAddedTypes(filteredTypes);
        setSectionsIsValid(prev => ({
          ...prev,
          [sectionName]: true,
        }));

        // Only for Discussion Section
        if (sectionName == 'discussion') {
          const validInfo = onValidateSections();
          const { isValid, type } = validInfo;
          const validation = !!type ? isValid : true;
          setBuildFormValid(validation);
          const sentData = {
            sectionName: 'discussion',
            buildFormValid: validation,
            data: {
              ...formBuilderStatesRef?.current,
              types: filteredTypes,
            },
          };
          if ((mode == 'edit' || mode == 'new') && values?.status != 'sent') {
            !!onValuesChange &&
              onValuesChange({ ...sentData, valsEqual: false });
          }
        }
      }
    },
    [
      onValuesChange,
      mode,
      values,
      formBuilderStatesRef,
      formBuilderAddedTypes,
    ],
  );

  const onValidateBuilderForm = useCallback(
    async (sectionName, sectionValues) => {
      let validbuildForm = false;
      if (
        !!sectionValues?.[sectionName] &&
        Object.keys(sectionValues[sectionName])?.length > 0 &&
        sectionValues[sectionName][`${sectionName}-fields`]?.length > 0 &&
        !!sectionValues[sectionName][`${sectionName}-fields`][0]
      ) {
        validbuildForm = true;
      } else {
        validbuildForm = false;
      }
      setSectionsIsValid(prev => ({
        ...prev,
        [sectionName]: validbuildForm,
      }));

      return {
        [sectionName]: validbuildForm,
      };
    },
    [sectionsIsValid],
  );

  const onChangeSectionHandler = useCallback(
    async (sectionName, sectionValues) => {
      const validateResult = await onValidateBuilderForm(
        sectionName,
        sectionValues,
      );
      // reset sections
      if (sectionValues == null) {
        setFormBuilderStates(prev => ({
          ...prev,
          [sectionName]: sectionValues,
        }));
      }
    },
    [],
  );

  // show section type
  const generateSection = useCallback(
    checkedType => {
      switch (checkedType) {
        case 'forms':
          return (
            <FBForms
              key="forms"
              mode={mode}
              method={method}
              initialValues={
                !!formBuilderStates?.[checkedType] &&
                formBuilderStates[checkedType]
              }
              isValidSection={sectionsIsValidRef?.current[checkedType]}
              setValidSection={onChangeSectioValidation}
              onDelete={deleteSectionType}
              onChangeSection={onChangeSectionHandler}
            />
          );
        case 'appointments':
          return (
            <FBAppointment
              key="appointments"
              mode={mode}
              method={method}
              initialValues={
                !!formBuilderStates?.[checkedType] &&
                formBuilderStates[checkedType]
              }
              isValidSection={sectionsIsValidRef?.current[checkedType]}
              setValidSection={onChangeSectioValidation}
              onDelete={deleteSectionType}
              onChangeSection={onChangeSectionHandler}
            />
          );
        case 'documents':
          return (
            <FBDocuments
              key="documents"
              mode={mode}
              method={method}
              initialValues={
                !!formBuilderStates?.[checkedType] &&
                formBuilderStates[checkedType]
              }
              isValidSection={sectionsIsValidRef?.current[checkedType]}
              setValidSection={onChangeSectioValidation}
              onDelete={deleteSectionType}
              onChangeSection={onChangeSectionHandler}
            />
          );
        case 'discussion':
          return <FBDiscussion mode={mode} onDelete={deleteSectionType} />;
      }
    },
    [
      formBuilderStates,
      sectionsIsValidRef,
      method,
      mode,
      formBuilderAddedTypes,
    ],
  );

  // On Change form send props to parent form "saveToForm" (if has it)
  // TODO: refactor is shit!!!
  const onFormChangeHandle = useCallback(
    async (formName, { changedFields, forms }) => {
     
      const form = forms[formName];
      const formValues = form?.getFieldsValue() || {};
      const updateFromVals = compareFormFieldsAndState(formValues) || {};

      const newData = {
        ...formBuilderStatesRef?.current,
        [formName]: updateFromVals[formName],
      };
      const sameVal = checkIsSameData(newData);

    //  console.log({ sameVal, sectionsIsValid, sectionsIsValidRef });

      setFormBuilderStates(newData);

      const sentData = {
        data: {
          ...formBuilderStatesRef?.current,
          [formName]: updateFromVals[formName],
          types: formBuilderAddedTypes,
        },
        sectionName: formName,
        buildFormValid: buildFormValidRef?.current,
      };

      if (
        sectionsIsValid[formName] &&
        !!updateFromVals[formName] &&
        (mode == ('edit' || 'new') && values?.status != 'sent')
      ) {
        // sent to parent component
        !!onValuesChange &&
          onValuesChange({ ...sentData, valsEqual: sameVal });
      }
    },
    [
      mode,
      values,
      onValuesChange,
      sectionsIsValid,
      buildFormValidRef,
      formBuilderStatesRef,
      formBuilderAddedTypes,
    ],
  );

  return (
    (mode == 'edit' || mode == 'new') && (
      <div className={childClassNames}>
        <Form.Provider onFormChange={onFormChangeHandle}>
          {formBuilderAddedTypes?.map(type => !!type && generateSection(type))}
          <FBAddNewSection
            buildFormValid={buildFormValid}
            values={formBuilderStates}
            parentStates={values}
            parentTypes={formBuilderAddedTypes}
            onChange={addNewTypeSectionHandler}
          />
        </Form.Provider>
      </div>
    )
  );
}

CommFormBuilder.defaultProps = {
  name: 'CommFormBuilder',
  mode: 'new',
  method: 'nouvel',
  initialValues: {},
};
CommFormBuilder.propTypes = {
  mode: PropTypes.oneOf(['edit', 'onlyRead', 'preview', 'new']).isRequired,
  method: PropTypes.oneOf(['update', 'nouvel']).isRequired,
  onSaveForm: PropTypes.func,
  onValuesChange: PropTypes.func,
};

export default memo(CommFormBuilder);
